import { cn } from '@/lib/utils'
import type { PaginationState, SortingState } from '@tanstack/react-table'
import {
  functionalUpdate,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import type { ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { FAIcon } from '../fa-icon'
import {
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui'

const DEFAULT_PAGE_SIZE = 25

interface PaginationOptions {
  pageSize?: number
  itemsText?: (n: number) => string
}

interface DataTableProps<TData extends { id: string }> {
  className?: string

  columns: ColumnDef<TData, any>[]
  data: TData[]

  noDataText?: string
  pagination?: boolean | PaginationOptions
  initialSort?: SortingState
  sorting?: SortingState
  onSortingChange?(sorting: SortingState): void
  /**
   * Leave the sorting itself to the caller — the table only renders the
   * headers and reports clicks. Use it when the rows arrive already sorted,
   * e.g. by the server, so the visible page is not re-sorted on its own.
   */
  manualSorting?: boolean
  hiddenColumns?: Record<string, boolean>

  caption?: ReactNode

  /** Extra classes for one row, e.g. to strike through a cancelled record. */
  rowClassName?(row: TData): string | undefined

  inCard?: boolean
  /**
   * Whether to show loading skeleton rows.
   * Set to a number to specify number of rows.
   * Defaults to `false`, with 3 rows as default when `true`.
   */
  loading?: boolean | number
}

/**
 * IMPORTANT! Because Tanstack Table currently does not support React Compiler,
 * columns and data must be stable between renders. Use useMemo where appropriate.
 */
export function DataTable<TData extends { id: string }>({
  className,

  columns,
  data,

  noDataText,
  pagination: paginationOpts = false,
  initialSort = [],
  sorting: controlledSorting,
  onSortingChange,
  manualSorting = false,
  hiddenColumns = {},

  caption,

  rowClassName,

  inCard = false,
  loading = false,
}: DataTableProps<TData>) {
  const translations = useTranslate({
    noData: 'components.data-table.no-data',
    pageNo: ['components.data-table.page-no', { page: 0, total: 0 }],
    items: ['components.data-table.items', { n: 0 }],
  })

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: !paginationOpts
      ? Infinity
      : paginationOpts === true
      ? DEFAULT_PAGE_SIZE
      : paginationOpts.pageSize ?? DEFAULT_PAGE_SIZE,
  })
  const [internalSorting, setInternalSorting] =
    useState<SortingState>(initialSort)

  const normalizedColumns = useMemo<ColumnDef<TData, any>[]>(
    () =>
      columns.map(col => ({
        ...col,
        enableSorting: col.enableSorting ?? false,
      })),
    [columns]
  )

  const columnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {}
    for (const [id, hide] of Object.entries(hiddenColumns))
      visibility[id] = !hide
    return visibility
  }, [hiddenColumns])

  const sorting = controlledSorting ?? internalSorting

  // Tanstack Table currently does not support React Compiler
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    columns: normalizedColumns,
    data,
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination: !paginationOpts ? undefined : pagination,
      columnVisibility,
      sorting,
    },
    onSortingChange: updater => {
      const nextSorting = functionalUpdate(updater, sorting)
      if (!controlledSorting) setInternalSorting(nextSorting)
      onSortingChange?.(nextSorting)
    },
    manualSorting,
    getSortedRowModel: getSortedRowModel(),
    ...(!paginationOpts
      ? {}
      : {
          autoResetPageIndex: false,
          getPaginationRowModel: getPaginationRowModel(),
          onPaginationChange: setPagination,
        }),
  })

  const paginationOptions = useMemo<PaginationOptions | null>(() => {
    if (!paginationOpts) return null

    return typeof paginationOpts === 'boolean' ? {} : paginationOpts
  }, [paginationOpts])

  const loadingConfig = useMemo(() => {
    if (loading === false || !!data.length) return null

    const cols = table
      .getAllColumns()
      .filter(col => col.getIsVisible())
      .map(col => ({
        id: col.id,
        narrow: !!col.columnDef.meta?.narrow,
      }))

    return {
      rows: Array.from(
        { length: typeof loading === 'number' && loading > 0 ? loading : 3 },
        (_, i) => i
      ),
      cols,
    }
  }, [data.length, loading, table])

  return (
    <div
      className={cn(
        inCard && 'px-4 rounded-xl bg-white shadow-card',
        className
      )}
    >
      <div className="overflow-hidden">
        <Table>
          {!!caption && <TableCaption>{caption}</TableCaption>}

          <TableHeader>
            {table.getHeaderGroups().map(group => (
              <TableRow key={group.id}>
                {group.headers.map(header => {
                  const center =
                    !!header.column.columnDef.meta?.center ||
                    !!header.column.columnDef.meta?.centerHeader
                  const end =
                    !!header.column.columnDef.meta?.end ||
                    !!header.column.columnDef.meta?.endHeader

                  const sortable = header.column.getCanSort()
                  const sortDir = header.column.getIsSorted()

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      center={center}
                      end={end}
                      sortable={sortable}
                      sortDir={sortDir}
                      onClick={
                        !sortable
                          ? undefined
                          : header.column.getToggleSortingHandler()
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {!!loadingConfig ? (
              loadingConfig.rows.map(row => (
                <TableRow key={row}>
                  {loadingConfig.cols.map(col => (
                    <TableCell key={col.id} narrow={col.narrow}>
                      <Skeleton className="h-8 w-full rounded-md" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  className={rowClassName?.(row.original)}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map(cell => {
                    const center =
                      !!cell.column.columnDef.meta?.center ||
                      !!cell.column.columnDef.meta?.centerCell
                    const end =
                      !!cell.column.columnDef.meta?.end ||
                      !!cell.column.columnDef.meta?.endCell
                    const narrow = !!cell.column.columnDef.meta?.narrow

                    return (
                      <TableCell
                        key={cell.id}
                        center={center}
                        end={end}
                        narrow={narrow}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  className="text-center text-gray-500"
                  colSpan={columns.length}
                >
                  {noDataText ?? translations.noData}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {!!paginationOptions && (
        <div className="flex items-center justify-end gap-4 py-4">
          <div>
            {paginationOptions.itemsText?.(data.length) ??
              translations.items({ n: data.length })}
          </div>

          <span>|</span>

          <div>
            {translations.pageNo({
              page: pagination.pageIndex + 1,
              total: Math.max(1, table.getPageCount()),
            })}
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon-sm"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              <FAIcon icon="chevron-left" />
            </Button>

            <Button
              variant="outline"
              size="icon-sm"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              <FAIcon icon="chevron-right" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
